import './index.scss';

import { Component } from 'react';
import { BraxButton, BraxIcon, BraxLoading, BraxModal, BraxModals, BraxModalsType, BraxToasts, BraxToastsType } from 'design-system';

import planPrices from '../../../variables/plan-prices';
import { PaymentInterval } from '../../../variables/payment-interval';
import t from '../../../utils/i18n';
import handleError from '../../../utils/errors';
import formatters from '../../../utils/formatters';
import { withContexts } from '../../../utils/with-context';
import Plan from '../../../view-model/plans/Plan';
import User from '../../../view-model/users/User';
import plansService from '../../../services/plans';
import analyticsService from '../../../services/analytics';
import UserContext from '../../../contexts/User';
import Checkout from '../Checkout';
import UpgradeUserPlan from '../UpgradeUserPlan';
import PlansFooter from '../../../components/PlansFooter';
import HorizontalContainer from '../../../components/HorizontalContainer';

export type PlansProps = {
    modals: BraxModalsType,
    toasts: BraxToastsType,
    user: User,
    open?: boolean,
    onClose?: () => void
};

type PlansState = {
    selected: PaymentInterval,
    plans: Plan[],
    fullFunctionalities: boolean,
    loading: boolean
};

export default withContexts({
    modals: BraxModals,
    toasts: BraxToasts,
    user: UserContext
}, class Plans extends Component<PlansProps, PlansState> {

    options: { type: PaymentInterval, label: string }[] = [{
        type: 'yearly',
        label: 'Plano anual'
    }, {
        type: 'monthly',
        label: 'Plano mensal'
    }];

    prices = { ...planPrices };

    state: PlansState = {
        selected: 'yearly',
        plans: [],
        fullFunctionalities: false,
        loading: true
    };

    async componentDidMount() {
        try {
            this.setState({
                plans: (await plansService.getPlans()).result,
                loading: false
            });
        } catch (error) {
            this.props.toasts.create({ type: 'destructive', ...handleError(error, 'Falha ao carregar os dados dos planos') });

            this.setState({ loading: false });
        }

        analyticsService.send({ user: this.props.user.id, event: 'opened_plans_screen' });
    }

    change(to: PlansState['selected']) {
        this.setState({ selected: to });
    }

    toggleFullFunctionalities() {
        this.setState(prevState => ({ fullFunctionalities: !prevState.fullFunctionalities }));
    }

    openCheckout(plan: Plan) {
        if (plan.isFree)
            return;

        if ((this.props.user.plan.isFree || this.props.user.plan.id < 1000) && !this.props.user.subscription?.isActive) {
            this.props.onClose?.();

            this.props.modals.custom(<Checkout plan={plan.id} interval={this.state.selected} />);
        } else {
            this.props.onClose?.();

            this.props.modals.custom(<UpgradeUserPlan plan={plan.id} interval={this.state.selected} />);
        }
    }

    render() {
        if (this.state.loading)
            return (<BraxLoading />);

        const signed = !this.props.user.plan.isFree || this.props.user.subscription?.isActive === true;

        const userPlan = this.props.user.plan.isFree && this.props.user.subscription?.isActive ? this.state.plans.find(plan => plan.id === this.props.user.subscription!.idPlan) || this.props.user.plan : this.props.user.plan;

        return (
            <BraxModal
                open={this.props.open}
                className="plans"
                size="full"
                onClose={this.props.onClose}
            >
                {/* <BraxLogo type="logotype" variant="primary" /> */}

                <h1>{ t('Planos, Funcionalidades e Preços') }</h1>

                <div className="buttons">
                    { this.options.map(option => (
                        <BraxButton
                            key={option.type}
                            variant="tertiary"
                            className={this.state.selected === option.type ? 'active' : ''}
                            onClick={() => this.change(option.type)}
                        >
                            { t(option.label) }
                        </BraxButton>
                    )) }
                </div>

                <HorizontalContainer>
                    { this.state.plans.map(plan => !plan.isFree || !signed ? (
                        <div key={plan.id} className="card">
                            <h3>{ plan.title }</h3>

                            <p className="hint">{ plan.description }</p>

                            <hr />

                            <ul>
                                <li className="hint success">
                                    <BraxIcon icon="check-rounded" />

                                    <span>{ plan.toursVirtualsCount === 999999999
                                        ? t('Tours ilimitados')
                                        : t('Total de %n tours virtuais', plan.toursVirtualsCount)
                                    }</span>
                                </li>

                                <li className="hint success">
                                    <BraxIcon icon="check-rounded" />

                                    <span>{ plan.scenesCount === 999999999
                                        ? t('Imagens 360 ilimitadas')
                                        : t('%n imagens 360 por tour', plan.scenesCount)
                                    }</span>
                                </li>

                                <li className={`hint ${plan.gsvProjectsCount > 0 ? 'success' : ''}`}>
                                    <BraxIcon icon={plan.gsvProjectsCount > 0 ? 'check-rounded' : 'close'} />

                                    <span>{ plan.gsvProjectsCount > 0
                                        ? t('%n projetos GSV por mês', plan.gsvProjectsCount)
                                        : t('Google Street View')
                                    }</span>
                                </li>

                                <li className={`hint ${plan.logoCount > 0 ? 'success' : ''}`}>
                                    <BraxIcon icon={plan.logoCount > 0 ? 'check-rounded' : 'close'} />

                                    <span>{ plan.logoCount === 999999999
                                        ? t('Logos ilimitados')
                                    : plan.logoCount > 0
                                        ? t('%n logos', plan.logoCount)
                                        : t('Logos')
                                    }</span>
                                </li>

                                <li className={`hint ${plan.allowHotSpots ? 'success' : ''}`}>
                                    <BraxIcon icon={plan.allowHotSpots ? 'check-rounded' : 'close'} />

                                    <span>{ t('Setas de navegação') }</span>
                                </li>

                                <li className={`hint ${!plan.allowAds ? 'success' : ''}`}>
                                    <BraxIcon icon={!plan.allowAds ? 'check-rounded' : 'close'} />

                                    <span>{ t('Sem anúncios') }</span>
                                </li>

                                <li className={`hint ${plan.allowPrivacyControll ? 'success' : ''}`}>
                                    <BraxIcon icon={plan.allowPrivacyControll ? 'check-rounded' : 'close'} />

                                    <span>{ t('Controle de privacidade') }</span>
                                </li>

                                <li className={`hint ${plan.allowDonwloadTour ? 'success' : ''}`}>
                                    <BraxIcon icon={plan.allowDonwloadTour ? 'check-rounded' : 'close'} />

                                    <span>{ t('Acesso offline no app') }</span>
                                </li>

                                <li className={`hint ${plan.allowAudioScene ? 'success' : ''}`}>
                                    <BraxIcon icon={plan.allowAudioScene ? 'check-rounded' : 'close'} />

                                    <span>{ t('Som ambiente') }</span>
                                </li>

                                <li className={`hint ${plan.allowImages ? 'success' : ''}`}>
                                    <BraxIcon icon={plan.allowImages ? 'check-rounded' : 'close'} />

                                    <span>{ t('Galeria de imagens') }</span>
                                </li>

                                <li className={`hint ${plan.allowFloorPlanImages ? 'success' : ''}`}>
                                    <BraxIcon icon={plan.allowFloorPlanImages ? 'check-rounded' : 'close'} />

                                    <span>{ t('Plantas baixas') }</span>
                                </li>

                                <li className={`hint ${plan.allowMarkers ? 'success' : ''}`}>
                                    <BraxIcon icon={plan.allowMarkers ? 'check-rounded' : 'close'} />

                                    <span>{ t('Marcadores de texto') }</span>
                                </li>
                            </ul>

                            { this.state.selected === 'monthly' ? (
                                <div className="price monthly">
                                    <p className="hint">
                                        <span>R$</span>

                                        <span>{ formatters.numberToMoney(this.prices[plan.id].monthly, false) }</span>

                                        <span>{ t('mês') }</span>
                                    </p>

                                    { !plan.isFree ? (
                                        <p className="hint">{ t('equivalente a R$ %s / ano', formatters.numberToMoney(this.prices[plan.id].monthlyYear, false)) }</p>
                                    ) : null }
                                </div>
                            ) : (
                                <div className="price yearly">
                                    <p className="hint">
                                        <span>R$</span>

                                        <span>{ formatters.numberToMoney(this.prices[plan.id].yearly, false) }</span>

                                        <span>{ t('ano') }</span>
                                    </p>

                                    { !plan.isFree ? (
                                        <p className="hint">{ t('à vista ou até 12x R$ %s', formatters.numberToMoney(this.prices[plan.id].yearlyMonth, false)) }</p>
                                    ) : null }
                                </div>
                            ) }

                            <BraxButton
                                variant="secondary"
                                disabled={plan.isFree || plan.id < userPlan.id || (plan.id === userPlan.id && (this.state.selected === 'monthly' || this.props.user.subscription?.type === 'yearly'))}
                                onClick={() => this.openCheckout(plan)}
                            >
                                { t(
                                    userPlan.id < 1000
                                        ? 'Assinar'
                                    : (!signed && plan.isFree) || (userPlan.id === plan.id && this.props.user.subscription?.type === this.state.selected)
                                        ? 'Assinado'
                                    : signed && (plan.id > userPlan.id || (plan.id === userPlan.id && this.state.selected === 'yearly'))
                                        ? 'Upgrade'
                                        : 'Assinar'
                                ) }
                            </BraxButton>
                        </div>
                    ) : null) }
                </HorizontalContainer>

                {/* <div className="details">
                    <div>
                        <p onClick={() => this.toggleFullFunctionalities()}>
                            { t(`Ver ${this.state.fullFunctionalities ? 'menos' : 'mais'} funcionalidades`) }
                        </p>

                        <Link to="/help">
                            <BraxIcon icon="video" />

                            <span>{ t('Aprenda a utilizar esta tela') }</span>
                        </Link>

                        <BraxButton variant="secondary">{ t('Consultar planos antigos') }</BraxButton>
                    </div>
                </div> */}

                <PlansFooter />

                <p className="copyright">{ t('© Todos os direitos reservados - {year}') }</p>
            </BraxModal>
        );
    }

});
